import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { db, functions, storage } from "../firebase";

type InboxThread = {
  id: string;
  type?: string;
  clubId?: string;
  title?: string;
  subtitle?: string;
  studentKeys?: string[];
  participantIds?: string[];
  replyMode?: string;
  createdBy?: string;
  lastMessageText?: string;
  lastMessageAt?: any;
  lastMessageSenderId?: string;
  lastMessageSenderName?: string;
  lastActivityAt?: any;
  lastReadAt?: any;
  schoolYear?: string;
  hasUnread?: boolean;
};

type MessageAttachment = {
  id: string;
  name: string;
  size: number;
  contentType: string;
  storagePath: string;
  downloadUrl?: string;
  createdAtIso: string;
};

type ChatMessage = {
  id: string;
  senderId: string;
  senderDisplayName?: string;
  text: string;
  createdAt?: any;
  attachments?: MessageAttachment[];
};

type AttachmentPolicy = {
  allowedContentTypes: string[];
  maxBytesMessaging: number;
  maxFilesPerMessage: number;
};

type PendingFile = {
  file: File;
  name: string;
  size: number;
  mimeType: string;
};

function normalizeString(v: any) {
  return typeof v === "string" ? v.trim() : "";
}


function normalizeEmail(v: any) {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

function preferredPersonName(data: any, fallback = ""): string {
  const display = normalizeString(data?.displayName);
  if (display) return display;

  const full = normalizeString(data?.fullName);
  if (full) return full;

  const name = normalizeString(data?.name);
  if (name) return name;

  const teacherName = normalizeString(data?.teacherName);
  if (teacherName) return teacherName;

  const parentName = normalizeString(data?.parentName);
  if (parentName) return parentName;

  const first = normalizeString(data?.firstName);
  const last = normalizeString(data?.lastName);
  const combo = `${first} ${last}`.trim();
  if (combo) return combo;

  return fallback;
}

async function firstDocByField(
  collectionName: "teachers" | "parents",
  field: string,
  value: string
): Promise<any | null> {
  if (!value) return null;
  try {
    const snap = await getDocs(
      query(collection(db, collectionName), where(field, "==", value), limit(1))
    );
    if (!snap.empty) return snap.docs[0].data();
  } catch {
    // ignore
  }
  return null;
}

async function resolveFriendlyNameForUid(uid: string): Promise<string | null> {
  try {
    const profileSnap = await getDoc(doc(db, "userProfiles", uid));

    let profileEmail = "";
    let teacherKey = "";
    let parentKey = "";

    if (profileSnap.exists()) {
      const profile = profileSnap.data() as any;

      const direct = preferredPersonName(profile, "");
      if (direct) return direct;

      profileEmail = normalizeEmail(profile?.email);
      teacherKey = normalizeString(profile?.teacherKey);
      parentKey = normalizeString(profile?.parentKey);

      if (teacherKey) {
        const teacherDirect = await getDoc(doc(db, "teachers", teacherKey));
        if (teacherDirect.exists()) {
          const nm = preferredPersonName(teacherDirect.data(), "");
          if (nm) return nm;
        }
      }

      if (parentKey) {
        const parentDirect = await getDoc(doc(db, "parents", parentKey));
        if (parentDirect.exists()) {
          const nm = preferredPersonName(parentDirect.data(), "");
          if (nm) return nm;
        }
      }
    }

    const byUidTeacher =
      (await firstDocByField("teachers", "uid", uid)) ||
      (await firstDocByField("teachers", "linkedUid", uid)) ||
      (await firstDocByField("teachers", "userUid", uid));
    if (byUidTeacher) {
      const nm = preferredPersonName(byUidTeacher, "");
      if (nm) return nm;
    }

    const byUidParent =
      (await firstDocByField("parents", "uid", uid)) ||
      (await firstDocByField("parents", "linkedUid", uid)) ||
      (await firstDocByField("parents", "userUid", uid));
    if (byUidParent) {
      const nm = preferredPersonName(byUidParent, "");
      if (nm) return nm;
    }

    if (teacherKey) {
      const byTeacherKey = await firstDocByField("teachers", "teacherKey", teacherKey);
      if (byTeacherKey) {
        const nm = preferredPersonName(byTeacherKey, "");
        if (nm) return nm;
      }
    }

    if (parentKey) {
      const byParentKey = await firstDocByField("parents", "parentKey", parentKey);
      if (byParentKey) {
        const nm = preferredPersonName(byParentKey, "");
        if (nm) return nm;
      }
    }

    if (profileEmail) {
      const byEmailTeacher =
        (await firstDocByField("teachers", "email", profileEmail)) ||
        (await firstDocByField("teachers", "linkedEmail", profileEmail));
      if (byEmailTeacher) {
        const nm = preferredPersonName(byEmailTeacher, "");
        if (nm) return nm;
      }

      const byEmailParent =
        (await firstDocByField("parents", "email", profileEmail)) ||
        (await firstDocByField("parents", "linkedEmail", profileEmail));
      if (byEmailParent) {
        const nm = preferredPersonName(byEmailParent, "");
        if (nm) return nm;
      }
    }

    return profileEmail || null;
  } catch {
    return null;
  }
}

function toMillisSafe(v: any): number {
  if (!v) return 0;
  try {
    if (typeof v?.toMillis === "function") return v.toMillis();
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
}

function formatWhen(v: any) {
  const ms = toMillisSafe(v);
  if (!ms) return "";
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  return sameDay
    ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function dispatchThreadRead(threadId: string, readAtMillis = Date.now()) {
  window.dispatchEvent(
    new CustomEvent("zca:thread-read", {
      detail: { threadId, readAtMillis },
    })
  );
}

function makeId() {
  return Math.random().toString(16).slice(2) + "-" + Date.now().toString(16);
}

function safeName(name: string) {
  const cleaned = name.replace(/[^\w.\-() ]+/g, "_").trim();
  return cleaned.length ? cleaned : "file";
}

function inferMimeTypeFromName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".webp")) return "image/webp";
  return "application/octet-stream";
}

function normalizeMimeType(mimeType: string, fileName: string): string {
  const mt = (mimeType || "").trim().toLowerCase();
  if (mt === "image/jpg") return "image/jpeg";
  if (!mt || mt === "application/octet-stream") return inferMimeTypeFromName(fileName);
  return mt;
}

async function loadAttachmentPolicy(): Promise<AttachmentPolicy> {
  const fallback: AttachmentPolicy = {
    allowedContentTypes: ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/jpg"],
    maxBytesMessaging: 5 * 1024 * 1024,
    maxFilesPerMessage: 3,
  };

  try {
    const snap = await getDoc(doc(db, "settings", "attachmentPolicy"));
    if (!snap.exists()) return fallback;
    const d = snap.data() as any;

    const allowed = Array.isArray(d.allowedContentTypes)
      ? d.allowedContentTypes
      : Array.isArray(d.allowedMimeTypes)
        ? d.allowedMimeTypes
        : fallback.allowedContentTypes;

    return {
      allowedContentTypes: allowed,
      maxBytesMessaging: Number(d.maxBytesMessaging ?? d.maxBytes ?? fallback.maxBytesMessaging),
      maxFilesPerMessage: Number(d.maxFilesPerMessage ?? fallback.maxFilesPerMessage),
    };
  } catch {
    return fallback;
  }
}

async function fetchInboxThreads(limit = 250): Promise<InboxThread[]> {
  const fn = httpsCallable(functions, "getInboxThreads");
  const res: any = await fn({ limit });
  const data = res?.data || {};
  const threads = Array.isArray(data.threads) ? data.threads : [];

  const auth = getAuth();
  const uid = auth.currentUser?.uid || "";

  return threads.map((t: any) => {
    const lastMessageMs = toMillisSafe(t.lastMessageAt);
    const lastRead = t?.lastReadAt?.[uid] ?? t?.lastReadAt;
    const lastReadMs = toMillisSafe(lastRead);
    const sentByMe = String(t?.lastMessageSenderId || "") === uid;
    const hasUnread =
      typeof t?.hasUnread === "boolean"
        ? t.hasUnread
        : !sentByMe && lastMessageMs > lastReadMs;

    return {
      ...t,
      hasUnread,
    };
  });
}

function subscribeToMessages(threadId: string, callback: (messages: ChatMessage[]) => void) {
  const msgsRef = collection(db, "threads", threadId, "messages");
  const q = query(msgsRef, orderBy("createdAt", "asc"));

  return onSnapshot(
    q,
    (snap) => {
      const items: ChatMessage[] = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data() as any;
        items.push({
          id: docSnap.id,
          senderId: data.senderId,
          senderDisplayName: data.senderDisplayName,
          text: data.text,
          createdAt: data.createdAt,
          attachments: Array.isArray(data.attachments) ? data.attachments : [],
        });
      });
      callback(items);
    },
    () => callback([])
  );
}

function useEmailNotificationPreference(
  userUid: string | null | undefined,
  field: "emailNotificationsEnabled" | "staffEmailNotificationsEnabled"
) {
  const [enabled, setEnabledState] = useState(false);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userUid) {
      setEnabledState(false);
      setReady(false);
      return;
    }

    setReady(false);
    const ref = doc(db, "userSettings", userUid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as any;
        const legacyMessageCopy =
          field === "emailNotificationsEnabled" && data?.teacherEmailNotificationsEnabled === true;
        setEnabledState(data?.[field] === true || legacyMessageCopy);
        setReady(true);
      },
      (err) => {
        console.warn("[settings] Failed to read email notification preference.", err);
        setReady(true);
      }
    );

    return () => unsub();
  }, [userUid, field]);

  async function setEnabled(next: boolean) {
    if (!userUid) return;
    const previous = enabled;
    setSaving(true);
    setEnabledState(next);

    try {
      await setDoc(
        doc(db, "userSettings", userUid),
        { [field]: next },
        { merge: true }
      );
    } catch (err) {
      setEnabledState(previous);
      throw err;
    } finally {
      setSaving(false);
    }
  }

  return { enabled, ready, saving, setEnabled };
}

async function createMessageDraftDoc(params: {
  threadId: string;
  senderId: string;
  senderDisplayName?: string;
  text?: string;
}) {
  const refDoc = await addDoc(collection(db, "threads", params.threadId, "messages"), {
    senderId: params.senderId,
    senderDisplayName: normalizeString(params.senderDisplayName) || "User",
    text: typeof params.text === "string" ? params.text : "",
    attachments: [],
    createdAt: serverTimestamp(),
  });

  return refDoc.id;
}

async function sendSimpleMessage(threadId: string, senderId: string, senderDisplayName: string, text: string) {
  await addDoc(collection(db, "threads", threadId, "messages"), {
    senderId,
    senderDisplayName,
    text: text.trim(),
    attachments: [],
    createdAt: serverTimestamp(),
  });
}

async function markThreadRead(threadId: string, userId: string) {
  try {
    const fn = httpsCallable(functions, "markInboxThreadRead");
    await fn({ threadId });
  } catch {
    const ts = serverTimestamp();
    try {
      await updateDoc(doc(db, "threads", threadId), {
        [`lastReadAt.${userId}`]: ts,
      } as any);
    } catch {
      // ignore
    }

    try {
      await updateDoc(doc(db, "userInboxes", userId, "threads", threadId), {
        [`lastReadAt.${userId}`]: ts,
        hasUnread: false,
        unreadCount: 0,
      } as any);
    } catch {
      // ignore
    }
  }
}

function applyLocalReadState(items: InboxThread[], selectedThreadId: string | null, localReadAtByThread: Record<string, number>) {
  return items.map((item) => {
    if (selectedThreadId === item.id) return { ...item, hasUnread: false };

    const localReadAt = localReadAtByThread[item.id] || 0;
    const lastMessageAt = toMillisSafe(item.lastMessageAt);
    if (!localReadAt || lastMessageAt > localReadAt) return item;
    return { ...item, hasUnread: false };
  });
}

export function MessagingPage() {
  const auth = getAuth();
  const { isAdmin, isSuperAdmin } = useAuth();
  const user = auth.currentUser;
  const emailPref = useEmailNotificationPreference(user?.uid, "emailNotificationsEnabled");
  const staffEmailPref = useEmailNotificationPreference(user?.uid, "staffEmailNotificationsEnabled");
  const nav = useNavigate();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const threadIdFromQuery = qs.get("threadId") || "";

  const [threads, setThreads] = useState<InboxThread[]>([]);
  const [localReadAtByThread, setLocalReadAtByThread] = useState<Record<string, number>>({});
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [threadFilter, setThreadFilter] = useState("");
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(threadIdFromQuery || null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [input, setInput] = useState("");
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [policy, setPolicy] = useState<AttachmentPolicy | null>(null);

  const [busySend, setBusySend] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [dragActive, setDragActive] = useState(false);
  const [participantNameMap, setParticipantNameMap] = useState<Record<string, string>>({});
  const [selectedThreadMeta, setSelectedThreadMeta] = useState<{ type?: string; replyMode?: string; createdBy?: string }>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messageEndRef = useRef<HTMLDivElement | null>(null);

  const selectedThread = useMemo(
    () => threads.find((t) => t.id === selectedThreadId) || null,
    [threads, selectedThreadId]
  );
  const canReplyToSelectedThread = useMemo(() => {
    if (!selectedThread) return false;
    const type = selectedThreadMeta.type || selectedThread.type;
    const replyMode = selectedThreadMeta.replyMode || selectedThread.replyMode;
    const createdBy = selectedThreadMeta.createdBy || selectedThread.createdBy;
    if (type !== "staffAnnouncement" && replyMode !== "none") return true;
    return createdBy === user?.uid || isAdmin || isSuperAdmin;
  }, [isAdmin, isSuperAdmin, selectedThread, selectedThreadMeta, user?.uid]);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!selectedThreadId) {
        setParticipantNameMap({});
        setSelectedThreadMeta({});
        return;
      }

      try {
        const snap = await getDoc(doc(db, "threads", selectedThreadId));
        if (!snap.exists()) {
          if (active) setParticipantNameMap({});
          return;
        }

        const data = snap.data() as any;
        if (active) {
          setSelectedThreadMeta({
            type: typeof data?.type === "string" ? data.type : undefined,
            replyMode: typeof data?.replyMode === "string" ? data.replyMode : undefined,
            createdBy: typeof data?.createdBy === "string" ? data.createdBy : undefined,
          });
        }
        const participantIds = Array.isArray(data?.participantIds) ? data.participantIds : [];

        if (!participantIds.length) {
          if (active) setParticipantNameMap({});
          return;
        }

        const pairs = await Promise.all(
          participantIds.map(async (uid: string) => {
            const name = await resolveFriendlyNameForUid(uid);
            return [uid, name] as const;
          })
        );

        const nextMap: Record<string, string> = {};
        for (const [uid, name] of pairs) {
          if (name) nextMap[uid] = name;
        }

        if (active) setParticipantNameMap(nextMap);
      } catch {
        if (active) setParticipantNameMap({});
      }
    })();

    return () => {
      active = false;
    };
  }, [selectedThreadId]);

  function senderNameForMessage(m: ChatMessage) {
    const mapped = participantNameMap[m.senderId || ""];
    if (mapped) return mapped;

    const raw = normalizeString(m.senderDisplayName);
    if (raw && !raw.includes("@")) return raw;

    return raw || m.senderId || "Unknown sender";
  }

  useEffect(() => {
    (async () => {
      setPolicy(await loadAttachmentPolicy());
    })();
  }, []);

  async function refreshThreads() {
    setLoadingThreads(true);
    setErr(null);
    try {
      const items = await fetchInboxThreads(250);
      setThreads(applyLocalReadState(items, selectedThreadId, localReadAtByThread));

      if (threadIdFromQuery) {
        const hit = items.find((t) => t.id === threadIdFromQuery);
        if (hit) {
          setSelectedThreadId(hit.id);
          return;
        }
      }

    } catch (e: any) {
      setErr(e?.message || "Failed to load inbox.");
      setThreads([]);
    } finally {
      setLoadingThreads(false);
    }
  }

  useEffect(() => {
    refreshThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadIdFromQuery]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      refreshThreads();
    }, 15000);

    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadIdFromQuery, selectedThreadId, user?.uid]);

  useEffect(() => {
    if (!selectedThreadId || !user?.uid) {
      setMessages([]);
      return;
    }

    window.dispatchEvent(
      new CustomEvent("zca:active-thread", {
        detail: { threadId: selectedThreadId },
      })
    );

    setLoadingMessages(true);
    const unsub = subscribeToMessages(selectedThreadId, (items) => {
      setMessages(items);
      setLoadingMessages(false);

      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 50);

      const readAtMillis = Date.now();
      setLocalReadAtByThread((prev) => ({
        ...prev,
        [selectedThreadId]: Math.max(prev[selectedThreadId] || 0, readAtMillis),
      }));
      dispatchThreadRead(selectedThreadId, readAtMillis);
      setThreads((prev) =>
        prev.map((t) =>
          t.id === selectedThreadId ? { ...t, hasUnread: false } : t
        )
      );

      markThreadRead(selectedThreadId, user.uid)
        .then(() => {
          setThreads((prev) =>
            prev.map((t) =>
              t.id === selectedThreadId ? { ...t, hasUnread: false } : t
            )
          );
        })
        .catch(() => {});
    });

    return () => {
      unsub();
      window.dispatchEvent(
        new CustomEvent("zca:active-thread", {
          detail: { threadId: null },
        })
      );
    };
  }, [selectedThreadId, user?.uid]);

  async function addPendingFiles(files: FileList | File[]) {
    const p = policy ?? (await loadAttachmentPolicy());
    const already = pendingFiles.length;
    const maxAllowed = Math.max(0, p.maxFilesPerMessage - already);

    const picked = Array.from(files).slice(0, maxAllowed);
    const next: PendingFile[] = [];

    for (const f of picked) {
      const mimeType = normalizeMimeType(f.type || "", f.name);
      const allowed = p.allowedContentTypes || [];
      const looksAllowed =
        allowed.length === 0 ||
        allowed.includes(mimeType) ||
        allowed.includes(f.type || "");

      if (!looksAllowed) {
        setErr(`Not allowed: ${f.name} (${mimeType || "unknown"})`);
        continue;
      }

      if (f.size > p.maxBytesMessaging) {
        setErr(`Too large: ${f.name}`);
        continue;
      }

      next.push({
        file: f,
        name: f.name,
        size: f.size,
        mimeType,
      });
    }

    setPendingFiles((prev) => [...prev, ...next]);
  }

  function removePending(index: number) {
    const next = [...pendingFiles];
    next.splice(index, 1);
    setPendingFiles(next);
  }

  async function uploadAttachments(threadId: string, messageId: string): Promise<MessageAttachment[]> {
    const out: MessageAttachment[] = [];

    for (const f of pendingFiles) {
      const attId = makeId();
      const normalizedType = normalizeMimeType(f.mimeType, f.name);
      const fileName = `${attId}-${safeName(f.name)}`;
      const path = `messages/${threadId}/${messageId}/${fileName}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, f.file, { contentType: normalizedType });
      const url = await getDownloadURL(storageRef);

      out.push({
        id: attId,
        name: f.name,
        size: f.size,
        contentType: normalizedType,
        storagePath: path,
        downloadUrl: url,
        createdAtIso: new Date().toISOString(),
      });
    }

    return out;
  }

  async function onSend() {
    if (!selectedThreadId || !user?.uid) return;
    if (!canReplyToSelectedThread) {
      setErr("Replies are disabled for this staff announcement.");
      return;
    }

    const text = input.trim();
    const hasText = text.length > 0;
    const hasAtt = pendingFiles.length > 0;

    if (!hasText && !hasAtt) return;

    setBusySend(true);
    setErr(null);
    setMsg(null);

    try {
      const senderDisplayName =
        (await resolveFriendlyNameForUid(user.uid)) || user.displayName || user.email || "User";

      if (!hasAtt) {
        await sendSimpleMessage(selectedThreadId, user.uid, senderDisplayName, text);
      } else {
        const msgId = await createMessageDraftDoc({
          threadId: selectedThreadId,
          senderId: user.uid,
          senderDisplayName,
          text: "",
        });

        const atts = await uploadAttachments(selectedThreadId, msgId);

        await updateDoc(doc(db, "threads", selectedThreadId, "messages", msgId), {
          text,
          attachments: atts,
          createdAt: serverTimestamp(),
        });

        try {
          await updateDoc(doc(db, "threads", selectedThreadId), {
            lastMessageText:
              text || (atts.length === 1 ? "📎 Attachment" : `📎 ${atts.length} attachments`),
            lastMessageAt: serverTimestamp(),
            lastMessageSenderId: user.uid,
            lastMessageSenderName: senderDisplayName,
          });
        } catch {
          // ignore
        }
      }

      setInput("");
      setPendingFiles([]);
      setMsg("Message sent.");
      await refreshThreads();
      setTimeout(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 50);
    } catch (e: any) {
      setErr(e?.message || "Failed to send message.");
    } finally {
      setBusySend(false);
    }
  }

  const filteredThreads = useMemo(() => {
    const f = threadFilter.trim().toLowerCase();
    if (!f) return threads;

    return threads.filter((t) => {
      const hay = [
        t.title,
        t.type,
        t.clubId,
        t.lastMessageText,
        ...(Array.isArray(t.studentKeys) ? t.studentKeys : []),
        ...(Array.isArray(t.participantIds) ? t.participantIds : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(f);
    });
  }, [threads, threadFilter]);

  const unreadThreadCount = useMemo(
    () => threads.filter((t) => t.hasUnread === true).length,
    [threads]
  );

  useEffect(() => {
    const baseTitle = "Zion Classical Academy Admin";
    document.title = unreadThreadCount > 0
      ? `(${unreadThreadCount}) ${baseTitle}`
      : baseTitle;

    return () => {
      document.title = baseTitle;
    };
  }, [unreadThreadCount]);

  if (!user) {
    return (
      <div className="card">
        <h2>Messaging</h2>
        <div className="muted">You must be signed in.</div>
      </div>
    );
  }

  return (
    <div className="card messaging-shell">
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h2 style={{ marginTop: 0 }}>Messaging</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            Send and receive the same messaging threads used by the mobile app.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <label className="messaging-pref" title="Send no-reply email copies of new messages to your account email.">
            <input
              type="checkbox"
              checked={emailPref.enabled}
              disabled={!emailPref.ready || emailPref.saving}
              onChange={(e) => {
                emailPref.setEnabled(e.currentTarget.checked).catch((err) => {
                  console.warn("[settings] Failed to update email notification preference.", err);
                  setErr("Could not update email notification preference.");
                });
              }}
            />
            <span>
              <strong>Email copies</strong>
              <span className="muted">No-reply copies of new messages</span>
            </span>
          </label>
          <label className="messaging-pref" title="Send no-reply email copies of staff-to-staff messages to your account email.">
            <input
              type="checkbox"
              checked={staffEmailPref.enabled}
              disabled={!staffEmailPref.ready || staffEmailPref.saving}
              onChange={(e) => {
                staffEmailPref.setEnabled(e.currentTarget.checked).catch((err) => {
                  console.warn("[settings] Failed to update staff email notification preference.", err);
                  setErr("Could not update staff email notification preference.");
                });
              }}
            />
            <span>
              <strong>Staff email copies</strong>
              <span className="muted">No-reply copies of staff messages</span>
            </span>
          </label>
          <button className="btn" onClick={() => nav("/messaging/new-direct")}>
            New Direct Message
          </button>
          <button className="btn" onClick={() => nav("/messaging/new-staff")}>
            New Staff Message
          </button>
          <button className="btn secondary" onClick={() => nav("/messaging/new-announcement")}>
            New Announcement
          </button>
          {isSuperAdmin ? (
            <button className="btn secondary" onClick={() => window.location.assign("/messaging/thread-debug")}>
              Thread Access Diagnostics
            </button>
          ) : null}
          <button className="btn secondary" onClick={() => refreshThreads()}>
            Refresh Inbox
          </button>
        </div>
      </div>

      {msg ? <div className="success">{msg}</div> : null}
      {err ? <div className="error">{err}</div> : null}

      <div className="messaging-layout"
        style={{
          display: "grid",
          gridTemplateColumns: "360px 1fr",
          gap: 16,
          marginTop: 16,
          minHeight: 640,
        }}
      >
        <div className="card messaging-sidebar" style={{ padding: 12, background: "var(--surface)" }}>
          <div className="label">Inbox filter</div>
          <input
            className="input"
            placeholder="title, type, student, participant…"
            value={threadFilter}
            onChange={(e) => setThreadFilter(e.target.value)}
          />

          <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            {filteredThreads.length} / {threads.length} threads
          </div>

          <hr className="hr" />

          <div className="thread-list" style={{ display: "grid", gap: 8, maxHeight: 560, overflow: "auto" }}>
            {loadingThreads ? (
              <div className="muted">Loading inbox…</div>
            ) : filteredThreads.length === 0 ? (
              <div className="muted">No threads found.</div>
            ) : (
              filteredThreads.map((t) => {
                const selected = t.id === selectedThreadId;
                return (
                  <button
                    className="thread-list-item"
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedThreadId(t.id)}
                    style={{
                      textAlign: "left",
                      borderRadius: 12,
                      border: selected
                        ? "2px solid var(--primary)"
                        : t.hasUnread
                          ? "2px solid var(--danger)"
                          : "1px solid var(--border)",
                      background: selected
                        ? "var(--primary-soft)"
                        : t.hasUnread
                          ? "var(--danger-soft)"
                          : "var(--card)",
                      padding: 12,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div style={{
                        fontWeight: 800,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: t.hasUnread ? "var(--danger)" : undefined,
                      }}>
                        {t.title || t.type || t.id}
                      </div>
                      <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        {formatWhen(t.lastMessageAt || t.lastActivityAt)}
                      </div>
                    </div>

                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {t.subtitle || `${t.type || "direct"}${t.schoolYear ? ` • ${t.schoolYear}` : ""}`}
                    </div>

                    <div style={{ marginTop: 6, fontSize: 13 }}>
                      {t.lastMessageText || "No messages yet."}
                    </div>

                    {t.hasUnread ? (
                      <div style={{ marginTop: 8 }}>
                        <span className="pill unread">Unread</span>
                      </div>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="card messaging-preview" style={{ padding: 12, display: "flex", flexDirection: "column", minHeight: 640 }}>
          {!selectedThread ? (
            <div className="muted">Select a thread from the inbox.</div>
          ) : (
            <>
              <div style={{ borderBottom: "1px solid var(--border)", paddingBottom: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 20, fontWeight: 900 }}>
                  {selectedThread.title || selectedThread.type || selectedThread.id}
                </div>
                {selectedThread.subtitle ? (
                  <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                    {selectedThread.subtitle}
                  </div>
                ) : null}
                <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                  Thread ID: <code>{selectedThread.id}</code>
                </div>
                <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                  Type: <b>{selectedThread.type || "direct"}</b>
                  {selectedThread.schoolYear ? <> • School Year: <b>{selectedThread.schoolYear}</b></> : null}
                </div>
              </div>

              <div
                className="message-stream"
                style={{
                  flex: 1,
                  overflow: "auto",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  padding: 12,
                  background: "var(--surface)",
                }}
              >
                {loadingMessages ? (
                  <div className="muted">Loading messages…</div>
                ) : messages.length === 0 ? (
                  <div className="muted">No messages yet.</div>
                ) : (
                  messages.map((m) => {
                    const mine = m.senderId === user.uid;
                    const attachments = Array.isArray(m.attachments) ? m.attachments : [];

                    return (
                      <div
                        key={m.id}
                        className={`message-row ${mine ? "mine" : "theirs"}`}
                        style={{
                          display: "flex",
                          justifyContent: mine ? "flex-end" : "flex-start",
                          marginBottom: 10,
                        }}
                      >
                        <div
                          className={`message-bubble ${mine ? "mine" : "theirs"}`}
                          style={{
                            maxWidth: 520,
                            borderRadius: 14,
                            padding: 12,
                            background: mine ? "var(--primary)" : "var(--card)",
                            border: mine ? "none" : "1px solid var(--border)",
                            color: mine ? "white" : "var(--text)",
                          }}
                        >
                          {!mine ? (
                            <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6, opacity: 0.8 }}>
                              {senderNameForMessage(m)}
                            </div>
                          ) : null}

                          {m.text ? <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div> : null}

                          {attachments.length ? (
                            <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                              {attachments.map((a) => {
                                const isImage = (a.contentType || "").toLowerCase().startsWith("image/");
                                return (
                                  <div
                                    key={a.id}
                                    style={{
                                      borderRadius: 10,
                                      padding: 8,
                                      background: mine ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.04)",
                                    }}
                                  >
                                    {isImage && a.downloadUrl ? (
                                      <div style={{ marginBottom: 8 }}>
                                        <img
                                          src={a.downloadUrl}
                                          alt={a.name}
                                          style={{ maxWidth: 220, maxHeight: 180, borderRadius: 8, display: "block" }}
                                        />
                                      </div>
                                    ) : null}

                                    <a
                                      href={a.downloadUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        color: mine ? "white" : "var(--primary)",
                                        textDecoration: "none",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {a.name}
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}

                          <div style={{ marginTop: 8, fontSize: 11, opacity: 0.8 }}>
                            {formatWhen(m.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

                <div ref={messageEndRef} />
              </div>

              {!canReplyToSelectedThread ? (
                <div className="notice-light" style={{ marginTop: 12 }}>
                  <b>One-way staff announcement</b>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Replies are disabled for recipients.
                  </div>
                </div>
              ) : null}

              <div
                className="composer-shell"
                style={{
                  marginTop: 12,
                  opacity: canReplyToSelectedThread ? 1 : 0.65,
                  pointerEvents: canReplyToSelectedThread ? "auto" : "none",
                }}
              >
                <div className="label">Attachments</div>

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={async (e) => {
                    e.preventDefault();
                    setDragActive(false);
                    if (e.dataTransfer?.files?.length) {
                      await addPendingFiles(e.dataTransfer.files);
                    }
                  }}
                  style={{
                    border: dragActive ? "2px dashed var(--primary)" : "2px dashed var(--border-strong)",
                    borderRadius: 12,
                    padding: 14,
                    background: dragActive ? "var(--primary-soft)" : "var(--surface)",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 800 }}>Drag files here</div>
                  <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                    or use the picker. Allowed types and limits come from Attachment Policy.
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose Files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const files = e.target.files;
                        if (files?.length) await addPendingFiles(files);
                        e.currentTarget.value = "";
                      }}
                    />
                  </div>
                </div>

                {pendingFiles.length ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    {pendingFiles.map((f, idx) => (
                      <div
                        key={`${f.name}-${idx}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          border: "1px solid var(--border)",
                          borderRadius: 999,
                          padding: "8px 10px",
                          background: "var(--card)",
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{f.name}</span>
                        <span className="muted" style={{ fontSize: 12 }}>
                          {(f.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                        <button
                          type="button"
                          className="btn small secondary"
                          onClick={() => removePending(idx)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div style={{ display: "grid", gap: 10 }}>
                  <textarea
                    className="input"
                    rows={4}
                    placeholder="Type a message…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                  />

                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                      className="btn secondary"
                      type="button"
                      onClick={() => {
                        setInput("");
                        setPendingFiles([]);
                        setErr(null);
                        setMsg(null);
                      }}
                      disabled={busySend}
                    >
                      Clear
                    </button>

                    <button
                      className="btn"
                      type="button"
                      onClick={onSend}
                      disabled={busySend || !canReplyToSelectedThread || (!input.trim() && pendingFiles.length === 0)}
                    >
                      {busySend ? "Sending…" : "Send"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
